import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  Invoice,
  InvoiceDocumentType,
  InvoiceSource,
  InvoiceStatus,
  InvoiceTotals,
  PaginatedResult,
} from '@catalyst-psa/shared';
import {
  DEFAULT_PAGE_SIZE,
  INVOICE_DOCUMENT_TYPE_LABELS,
  INVOICE_SOURCES,
  INVOICE_STATUSES,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';
import { useAuth } from '../../auth/authContext.js';
import { DetailOverlay } from '../../components/layout/DetailOverlay.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { formatDate } from '../../utils/date.js';
import { useRowLink } from '../../utils/useRowLink.js';
import { rememberInvoiceSequence } from './invoiceSequence.js';
import { MemberMultiFilter } from '../members/MemberMultiFilter.js';
import { FilterDefaultsButtons } from '../filters/FilterDefaultsButtons.js';
import {
  savedBoolean,
  savedString,
  savedStringArray,
  useFilterDefaults,
} from '../filters/useFilterDefaults.js';
import {
  useBulkSendToReview,
  useBulkSetInvoiceDate,
  useInvoiceReviewers,
  useInvoices,
  useInvoiceTotals,
} from './api.js';
import { InvoiceFormModal } from './InvoiceFormModal.js';
import {
  INVOICE_PAYMENT_STATUS_LABELS,
  INVOICE_PAYMENT_STATUS_TONES,
  INVOICE_SOURCE_LABELS,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_TONES,
} from './status.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** One-click reviewer-filter buttons rendered next to the reviewers filter. */
const QUICK_REVIEWER_NAMES = ['Jeff', 'Damon'] as const;

/** Sort keys accepted by the backend invoice list (see INVOICE_SORTS). */
type InvoiceSortKey =
  | 'seq'
  | 'documentType'
  | 'customer'
  | 'source'
  | 'serviceCategory'
  | 'status'
  | 'reviewer'
  | 'periodStart'
  | 'issueDate'
  | 'dueDate'
  | 'total';

type InvoiceSort = { key: InvoiceSortKey; dir: 'asc' | 'desc' };

/** Clickable column header that toggles server-side sorting (sorts across all pages). */
function SortableHeader({
  label,
  sortKey,
  sort,
  onToggle,
  align = 'left',
}: {
  label: string;
  sortKey: InvoiceSortKey;
  sort: InvoiceSort | null;
  onToggle: (key: InvoiceSortKey) => void;
  align?: 'left' | 'right';
}): JSX.Element {
  const active = sort?.key === sortKey;
  const indicator = active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕';
  return (
    <th className={`px-4 py-3 font-medium ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide ${
          align === 'right' ? 'flex-row-reverse' : ''
        } ${active ? 'text-slate-700' : 'hover:text-slate-700'}`}
        aria-label={`Sort by ${label}`}
      >
        {label}
        <span className={`text-[10px] ${active ? 'text-slate-700' : 'text-slate-300'}`}>
          {indicator}
        </span>
      </button>
    </th>
  );
}

export function InvoicesPage(): JSX.Element {
  const navigate = useNavigate();
  const rowLink = useRowLink();
  const { can } = useAuth();
  const canEdit = can('invoices', 'edit');

  const [page, setPage] = useState(1);
  // 'needs_change' is a pseudo-status: it filters on the needs-change flag instead.
  const [status, setStatus] = useState<InvoiceStatus | 'needs_change' | ''>('');
  const [source, setSource] = useState<InvoiceSource | ''>('');
  // Free-text search box: matches the customer name or any portion of the invoice number.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [activeReviewerIds, setActiveReviewerIds] = useState<string[]>([]);
  // Invoice-date range. `month` is set when the range covers exactly one calendar month
  // (the arrows / This month), and cleared once either date is edited by hand.
  const [month, setMonth] = useState<string | null>(() => currentMonth());
  const [issueDateFrom, setIssueDateFrom] = useState(() => monthStart(currentMonth()));
  const [issueDateTo, setIssueDateTo] = useState(() => monthEnd(currentMonth()));
  const [createType, setCreateType] = useState<InvoiceDocumentType | null>(null);
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectingAll, setSelectingAll] = useState(false);
  const [bulkReviewerId, setBulkReviewerId] = useState('');
  const [bulkInvoiceDate, setBulkInvoiceDate] = useState('');
  const [bulkDateResult, setBulkDateResult] = useState<string | null>(null);
  // $0 invoices are hidden by default; the checkbox switches to showing ONLY $0 invoices.
  const [zeroOnly, setZeroOnly] = useState(false);
  const [sort, setSort] = useState<InvoiceSort | null>(null);

  const filterDefaults = useFilterDefaults('invoices', (saved) => {
    const savedStatus = savedString(saved, 'status');
    if (savedStatus !== null) setStatus(savedStatus as InvoiceStatus | 'needs_change' | '');
    const savedSource = savedString(saved, 'source');
    if (savedSource !== null) setSource(savedSource as InvoiceSource | '');
    const savedSearch = savedString(saved, 'search');
    if (savedSearch !== null) {
      setSearchInput(savedSearch);
      setSearch(savedSearch);
    }
    const savedReviewers = savedStringArray(saved, 'activeReviewerIds');
    if (savedReviewers !== null) setActiveReviewerIds(savedReviewers);
    const savedZero = savedBoolean(saved, 'zeroOnly');
    if (savedZero !== null) setZeroOnly(savedZero);
  });

  // Debounce the free-text search so each keystroke doesn't refetch.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filters = {
    status: status && status !== 'needs_change' ? status : undefined,
    needsChange: status === 'needs_change' ? true : undefined,
    source: source || undefined,
    search: search || undefined,
    activeReviewerIds: activeReviewerIds.length > 0 ? activeReviewerIds.join(',') : undefined,
    issueDateFrom: issueDateFrom || undefined,
    issueDateTo: issueDateTo || undefined,
    excludeZeroTotal: zeroOnly ? undefined : true,
    zeroTotalOnly: zeroOnly ? true : undefined,
  };
  const sortParams = sort ? { sortBy: sort.key, sortDir: sort.dir } : {};

  const query = useInvoices({ page, pageSize: DEFAULT_PAGE_SIZE, ...filters, ...sortParams });
  const totalsQuery = useInvoiceTotals(filters);

  const toggleSort = (key: InvoiceSortKey): void => {
    setPage(1);
    setSort((prev) =>
      prev && prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );
  };

  const goToMonth = (next: string): void => {
    setPage(1);
    setMonth(next);
    setIssueDateFrom(monthStart(next));
    setIssueDateTo(monthEnd(next));
  };
  const clearDateRange = (): void => {
    setPage(1);
    setMonth(null);
    setIssueDateFrom('');
    setIssueDateTo('');
  };

  // Export every invoice matching the current filters (across all pages), not just the
  // visible page. Walks the list endpoint in 200-row pages then builds a CSV.
  const handleExport = async (): Promise<void> => {
    setExporting(true);
    try {
      const all: Invoice[] = [];
      for (let p = 1; p <= 1000; p++) {
        const res = await api.get<PaginatedResult<Invoice>>(
          `/invoices${buildQuery({ page: p, pageSize: 200, ...filters, ...sortParams })}`,
        );
        all.push(...res.data);
        if (res.data.length === 0 || all.length >= res.page.totalItems) break;
      }
      downloadCsv(`invoices-${new Date().toISOString().slice(0, 10)}.csv`, buildInvoicesCsv(all));
    } finally {
      setExporting(false);
    }
  };

  const reviewersQuery = useInvoiceReviewers();
  const reviewers = reviewersQuery.data ?? [];
  const bulkSend = useBulkSendToReview();
  const bulkSetDate = useBulkSetInvoiceDate();

  const invoices = query.data?.data ?? [];
  const pageInfo = query.data?.page;
  const hasFilters = Boolean(
    status || source || search || activeReviewerIds.length > 0 || issueDateFrom || issueDateTo,
  );

  // Only draft / in-review invoices can be routed to a reviewer, so only those are selectable.
  const selectableIds = useMemo(
    () =>
      invoices
        .filter((i) => i.status === 'draft' || i.status === 'in_review')
        .map((i) => i.id),
    [invoices],
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const toggleOne = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // Select-all covers every filtered invoice across all pages, not just the visible page.
  // Walks the list endpoint in 200-row pages collecting the selectable (draft/in-review) ids.
  const toggleAll = async (): Promise<void> => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectingAll(true);
    try {
      const ids = new Set<string>();
      for (let p = 1; p <= 1000; p++) {
        const res = await api.get<PaginatedResult<Invoice>>(
          `/invoices${buildQuery({ page: p, pageSize: 200, ...filters })}`,
        );
        for (const inv of res.data) {
          if (inv.status === 'draft' || inv.status === 'in_review') ids.add(inv.id);
        }
        if (res.data.length === 0 || p * 200 >= res.page.totalItems) break;
      }
      setSelectedIds(ids);
    } finally {
      setSelectingAll(false);
    }
  };
  const handleBulkSend = (): void => {
    if (!bulkReviewerId || selectedIds.size === 0) return;
    bulkSend.mutate(
      { invoiceIds: [...selectedIds], reviewerId: bulkReviewerId },
      {
        onSuccess: () => {
          setSelectedIds(new Set());
          setBulkReviewerId('');
        },
      },
    );
  };
  const handleBulkSetDate = (): void => {
    if (!bulkInvoiceDate || selectedIds.size === 0) return;
    setBulkDateResult(null);
    bulkSetDate.mutate(
      { invoiceIds: [...selectedIds], invoiceDate: bulkInvoiceDate },
      {
        onSuccess: (result) => {
          setBulkDateResult(
            `Updated ${result.updated} invoice${result.updated === 1 ? '' : 's'}` +
              (result.skippedIds.length > 0
                ? ` · ${result.skippedIds.length} skipped (not editable)`
                : '') +
              '. Due dates recalculated.',
          );
          setSelectedIds(new Set());
          setBulkInvoiceDate('');
        },
      },
    );
  };
  const selectColSpan = canEdit ? 12 : 11;

  return (
    <div>
      <PageHeader
        title="Invoices"
        description="Manage created invoices: review them, send them, and track their status."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => void handleExport()}
              isLoading={exporting}
              disabled={exporting}
            >
              Export CSV
            </Button>
            {canEdit ? (
              <>
                <Button variant="secondary" onClick={() => setCreateType('invoice')}>
                  New invoice
                </Button>
                <Button variant="secondary" onClick={() => setCreateType('credit_memo')}>
                  New credit memo
                </Button>
                <Button onClick={() => navigate('/invoices/generate')}>Generate invoices</Button>
              </>
            ) : null}
          </div>
        }
      />

      <RevenueTotals
        totals={totalsQuery.data}
        isLoading={totalsQuery.isLoading}
        isError={totalsQuery.isError}
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-end gap-4 border-b border-slate-200 p-4">
          <div className="flex flex-col">
            <span className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
              Invoice date
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => goToMonth(shiftMonth(month ?? currentMonth(), -1))}
                aria-label="Previous month"
                title="Previous month"
                className="rounded-lg border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12.5 4.5 7 10l5.5 5.5" />
                </svg>
              </button>
              <span className="min-w-36 text-center text-sm font-medium text-slate-900">
                {month ? formatMonthLabel(month) : 'Custom range'}
              </span>
              <button
                type="button"
                onClick={() => goToMonth(shiftMonth(month ?? currentMonth(), 1))}
                aria-label="Next month"
                title="Next month"
                className="rounded-lg border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 4.5 13 10l-5.5 5.5" />
                </svg>
              </button>
            </div>
          </div>
          <label className="flex flex-col text-sm">
            <span className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
              From
            </span>
            <input
              type="date"
              value={issueDateFrom}
              max={issueDateTo || undefined}
              onChange={(e) => {
                setPage(1);
                setMonth(null);
                setIssueDateFrom(e.target.value);
              }}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </label>
          <label className="flex flex-col text-sm">
            <span className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
              To
            </span>
            <input
              type="date"
              value={issueDateTo}
              min={issueDateFrom || undefined}
              onChange={(e) => {
                setPage(1);
                setMonth(null);
                setIssueDateTo(e.target.value);
              }}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </label>
          <Button variant="ghost" onClick={() => goToMonth(currentMonth())}>
            This month
          </Button>
          <Button variant="ghost" onClick={clearDateRange}>
            All dates
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-3 border-b border-slate-200 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search customer or invoice #…"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <select
            value={source}
            onChange={(e) => {
              setPage(1);
              setSource(e.target.value as InvoiceSource | '');
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All kinds</option>
            {INVOICE_SOURCES.map((value) => (
              <option key={value} value={value}>
                {INVOICE_SOURCE_LABELS[value]}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value as InvoiceStatus | 'needs_change' | '');
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All statuses</option>
            {INVOICE_STATUSES.map((value) => (
              <option key={value} value={value}>
                {INVOICE_STATUS_LABELS[value]}
              </option>
            ))}
            <option value="needs_change">Needs change</option>
          </select>
          <div className="flex items-center gap-1.5">
            <div className="min-w-0 flex-1">
              <MemberMultiFilter
                selected={activeReviewerIds}
                onChange={(next) => {
                  setPage(1);
                  setActiveReviewerIds(next);
                }}
                allLabel="All reviewers"
              />
            </div>
            {QUICK_REVIEWER_NAMES.map((name) => {
              const member = reviewers.find((m) =>
                m.displayName.toLowerCase().includes(name.toLowerCase()),
              );
              const active =
                member !== undefined &&
                activeReviewerIds.length === 1 &&
                activeReviewerIds[0] === member.id;
              return (
                <button
                  key={name}
                  type="button"
                  disabled={!member}
                  onClick={() => {
                    if (!member) return;
                    setPage(1);
                    setActiveReviewerIds(active ? [] : [member.id]);
                  }}
                  className={`shrink-0 rounded border px-1.5 py-0.5 text-xs font-medium disabled:opacity-50 ${
                    active
                      ? 'border-brand-600 bg-brand-600 text-white'
                      : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={zeroOnly}
              onChange={(e) => {
                setPage(1);
                setZeroOnly(e.target.checked);
              }}
              className="h-4 w-4 rounded border-slate-300"
            />
            Only $0 invoices
          </label>
          <FilterDefaultsButtons
            handle={filterDefaults}
            onSave={() =>
              filterDefaults.save({ status, source, search, activeReviewerIds, zeroOnly })
            }
          />
        </div>

        {canEdit && selectedIds.size > 0 ? (
          <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
            <span className="text-sm font-medium text-slate-700">
              {selectedIds.size} selected
            </span>
            <select
              value={bulkReviewerId}
              onChange={(e) => setBulkReviewerId(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="">Choose reviewer…</option>
              {reviewers.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.displayName}
                </option>
              ))}
            </select>
            <Button
              onClick={handleBulkSend}
              disabled={!bulkReviewerId || bulkSend.isPending}
              isLoading={bulkSend.isPending}
            >
              Send to reviewer
            </Button>
            <Button variant="ghost" onClick={() => setSelectedIds(new Set())}>
              Clear
            </Button>
            {bulkSend.isError ? (
              <span className="text-sm text-rose-600">Failed to send. Please try again.</span>
            ) : null}
            <span className="mx-1 h-6 w-px bg-slate-300" aria-hidden="true" />
            <input
              type="date"
              value={bulkInvoiceDate}
              onChange={(e) => setBulkInvoiceDate(e.target.value)}
              aria-label="New invoice date"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <Button
              variant="secondary"
              onClick={handleBulkSetDate}
              disabled={!bulkInvoiceDate || bulkSetDate.isPending}
              isLoading={bulkSetDate.isPending}
              title="Set the invoice date on the selected invoices; due dates are recalculated from each invoice's net terms"
            >
              Set invoice date
            </Button>
            {bulkSetDate.isError ? (
              <span className="text-sm text-rose-600">
                Failed to update invoice dates. Please try again.
              </span>
            ) : null}
          </div>
        ) : null}
        {bulkDateResult ? (
          <div className="border-b border-slate-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
            {bulkDateResult}
          </div>
        ) : null}

        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {canEdit ? (
                <th className="px-4 py-3 font-medium">
                  <input
                    type="checkbox"
                    aria-label="Select all (all pages)"
                    title="Select every draft / in-review invoice matching the current filters, across all pages"
                    checked={allSelected}
                    onChange={() => void toggleAll()}
                    disabled={selectingAll || selectableIds.length === 0}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                </th>
              ) : null}
              <SortableHeader label="Number" sortKey="seq" sort={sort} onToggle={toggleSort} />
              <SortableHeader
                label="Type"
                sortKey="documentType"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader
                label="Customer"
                sortKey="customer"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader label="Kind" sortKey="source" sort={sort} onToggle={toggleSort} />
              <SortableHeader
                label="Category"
                sortKey="serviceCategory"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader label="Status" sortKey="status" sort={sort} onToggle={toggleSort} />
              <SortableHeader
                label="Reviewer"
                sortKey="reviewer"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader
                label="Period"
                sortKey="periodStart"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader
                label="Issued"
                sortKey="issueDate"
                sort={sort}
                onToggle={toggleSort}
              />
              <SortableHeader label="Due" sortKey="dueDate" sort={sort} onToggle={toggleSort} />
              <SortableHeader
                label="Total"
                sortKey="total"
                sort={sort}
                onToggle={toggleSort}
                align="right"
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={selectColSpan}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={invoices.length === 0}
              emptyTitle="No invoices found"
              emptyDescription={
                hasFilters ? 'Try a different filter.' : 'Generate invoices to get started.'
              }
            >
              {invoices.map((invoice) => {
                const selectable = invoice.status === 'draft' || invoice.status === 'in_review';
                return (
                  <tr
                    key={invoice.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClickCapture={() => rememberInvoiceSequence(invoices.map((i) => i.id))}
                    {...rowLink(`/invoices/${invoice.id}`)}
                  >
                    {canEdit ? (
                      <td
                        className="px-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                        role="presentation"
                      >
                        <input
                          type="checkbox"
                          aria-label={`Select ${invoice.number}`}
                          checked={selectedIds.has(invoice.id)}
                          disabled={!selectable}
                          onChange={() => toggleOne(invoice.id)}
                          className="h-4 w-4 rounded border-slate-300"
                        />
                      </td>
                    ) : null}
                    <td className="px-4 py-3 font-medium text-slate-900">{invoice.number}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {INVOICE_DOCUMENT_TYPE_LABELS[invoice.documentType]}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{invoice.customerName ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {INVOICE_SOURCE_LABELS[invoice.source]}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {invoice.serviceCategoryName ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge tone={INVOICE_STATUS_TONES[invoice.status]}>
                          {INVOICE_STATUS_LABELS[invoice.status]}
                        </Badge>
                        {invoice.needsChange ? <Badge tone="amber">Needs change</Badge> : null}
                        {invoice.paymentStatus ? (
                          <Badge tone={INVOICE_PAYMENT_STATUS_TONES[invoice.paymentStatus]}>
                            {INVOICE_PAYMENT_STATUS_LABELS[invoice.paymentStatus]}
                          </Badge>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {invoice.activeReviewerName ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {invoice.periodStart && invoice.periodEnd
                        ? `${formatDate(invoice.periodStart)} → ${formatDate(invoice.periodEnd)}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(invoice.issueDate)}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(invoice.dueDate)}</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900">
                      {currency.format(Number(invoice.total))}
                    </td>
                  </tr>
                );
              })}
            </DataState>
          </tbody>
        </table>

        {pageInfo && invoices.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <InvoiceFormModal
        isOpen={createType !== null}
        invoice={null}
        documentType={createType ?? 'invoice'}
        onClose={() => setCreateType(null)}
      />
      <DetailOverlay listPath="/invoices" />
    </div>
  );
}

/** Revenue totals for the current filters: a grand total plus one card per invoice source. */
function RevenueTotals({
  totals,
  isLoading,
  isError,
}: {
  totals: InvoiceTotals | undefined;
  isLoading: boolean;
  isError: boolean;
}): JSX.Element {
  const value = (amount: string | undefined): string => {
    if (isError) return '—';
    if (isLoading || !amount) return '…';
    return currency.format(Number(amount));
  };

  return (
    <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-6">
      <div className="rounded-xl border border-brand-200 bg-brand-50 p-4 shadow-sm">
        <div className="text-xs font-medium uppercase tracking-wide text-brand-700">
          Grand total
        </div>
        <div className="mt-1 text-xl font-semibold text-brand-900">
          {value(totals?.grandTotal)}
        </div>
      </div>
      {INVOICE_SOURCES.map((invoiceSource) => (
        <div
          key={invoiceSource}
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
        >
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {INVOICE_SOURCE_LABELS[invoiceSource]}
          </div>
          <div className="mt-1 text-xl font-semibold text-slate-900">
            {value(totals?.bySource[invoiceSource])}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Current calendar month as `YYYY-MM` (local time). */
function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** First day of a `YYYY-MM` month, as `YYYY-MM-DD`. */
function monthStart(month: string): string {
  return `${month}-01`;
}

/** Last day of a `YYYY-MM` month, as `YYYY-MM-DD`. */
function monthEnd(month: string): string {
  const year = Number(month.slice(0, 4));
  const monthNumber = Number(month.slice(5, 7));
  const lastDay = new Date(year, monthNumber, 0).getDate();
  return `${month}-${String(lastDay).padStart(2, '0')}`;
}

/** Moves a `YYYY-MM` month by `delta` months. */
function shiftMonth(month: string, delta: number): string {
  const year = Number(month.slice(0, 4));
  const monthNumber = Number(month.slice(5, 7));
  const shifted = new Date(year, monthNumber - 1 + delta, 1);
  return `${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, '0')}`;
}

/** Renders a `YYYY-MM` month as e.g. "July 2026". */
function formatMonthLabel(month: string): string {
  const year = Number(month.slice(0, 4));
  const monthNumber = Number(month.slice(5, 7));
  return new Date(year, monthNumber - 1, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

/** Escapes a value for a CSV cell (RFC 4180: quote when it contains a comma/quote/newline). */
function csvField(value: unknown): string {
  const text = value == null ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Builds a CSV of the invoice list (one row per invoice) with the on-screen + key billing fields. */
function buildInvoicesCsv(invoices: Invoice[]): string {
  const header = [
    'number',
    'documentType',
    'customer',
    'kind',
    'serviceCategory',
    'status',
    'reviewer',
    'needsChange',
    'periodStart',
    'periodEnd',
    'issueDate',
    'dueDate',
    'currency',
    'subtotal',
    'total',
    'terms',
    'createdExternally',
    'agreementId',
    'projectId',
    'notes',
    'internalNotes',
    'createdAt',
  ];
  const rows: string[] = [header.map(csvField).join(',')];
  for (const inv of invoices) {
    rows.push(
      [
        inv.number,
        INVOICE_DOCUMENT_TYPE_LABELS[inv.documentType],
        inv.customerName ?? '',
        INVOICE_SOURCE_LABELS[inv.source],
        inv.serviceCategoryName ?? '',
        INVOICE_STATUS_LABELS[inv.status],
        inv.activeReviewerName ?? '',
        inv.needsChange ? 'yes' : 'no',
        inv.periodStart ?? '',
        inv.periodEnd ?? '',
        inv.issueDate ?? '',
        inv.dueDate ?? '',
        inv.currency,
        inv.subtotal,
        inv.total,
        inv.terms ?? '',
        inv.createdExternally ? 'yes' : 'no',
        inv.agreementId ?? '',
        inv.projectId ?? '',
        inv.notes ?? '',
        inv.internalNotes ?? '',
        inv.createdAt,
      ]
        .map(csvField)
        .join(','),
    );
  }
  return rows.join('\r\n');
}

/** Triggers a browser download of a CSV string. */
function downloadCsv(fileName: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
